import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon, Button, Badge } from '@/components/atoms';
import { Pagination } from '@/components/molecules';
import { Modal } from '@/components/molecules/Modal';
import { ReportFilters, ReportSummaryCards, ReportTable } from '@/components/organisms/Reports';
import type { TableColumn } from '@/components/organisms/Reports';
import { useSalesReport, useExportReportMutation, useWarehousesList } from '@/hooks/useErpApi';
import type { SalesReportFilter, SalesReportRecord } from '@kisanlink/erp-service';

export default function SalesReportPage() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<SalesReportFilter>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [activeTab, setActiveTab] = useState<string>('all');
  const [selectedRecord, setSelectedRecord] = useState<SalesReportRecord & {
    customer_name?: string;
    landing_price?: number;
    purchase_value?: number;
    cgst_amount?: number;
    sgst_amount?: number;
    igst_amount?: number;
    tax_exempt_amount?: number;
    effective_tax_rate?: number;
    gross_margin_percent?: number;
    per_unit_margin?: number;
  } | null>(null);
  
  // Calculate offset for API call
  const offset = (currentPage - 1) * itemsPerPage;

  const queryFilters: SalesReportFilter = {
    ...filters,
    limit: itemsPerPage,
    offset,
    ...(activeTab !== 'all' && { status: activeTab }),
  };

  const { data: report, isLoading, error, refetch } = useSalesReport(queryFilters);
  const { data: warehouses } = useWarehousesList({});
  const { mutateAsync: exportReport, isLoading: isExporting } = useExportReportMutation();

  const warehouseOptions = (warehouses || []).map((w) => ({ id: w.id, name: w.name }));

  const handleFilterChange = useCallback((newFilters: Record<string, unknown>) => {
    setFilters(newFilters as SalesReportFilter);
    setCurrentPage(1);
  }, []);
  
  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    setCurrentPage(1);
  };

  const handleExport = useCallback(
    async (format: 'xlsx' | 'pdf') => {
      try {
        const blob = await exportReport({
          reportType: 'sales',
          filters: { ...queryFilters, format },
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `sales-report.${format}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } catch (err) {
        console.error('Export failed:', err);
      }
    },
    [queryFilters, exportReport]
  );

  const getStatusBadge = (status: string) => {
    const statusMap: Record<
      string,
      { variant: 'success' | 'warning' | 'error' | 'info' | 'neutral'; label: string }
    > = {
      pending: { variant: 'warning', label: 'Pending' },
      processing: { variant: 'info', label: 'Processing' },
      completed: { variant: 'success', label: 'Completed' },
      cancelled: { variant: 'error', label: 'Cancelled' },
    };
    const config = statusMap[status] || { variant: 'neutral' as const, label: status };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const getPaymentIcon = (mode: string): 'Banknote' | 'CreditCard' | 'Smartphone' | 'Globe' => {
    const modeMap: Record<string, 'Banknote' | 'CreditCard' | 'Smartphone' | 'Globe'> = {
      cash: 'Banknote',
      card: 'CreditCard',
      upi: 'Smartphone',
      online: 'Globe',
    };
    return modeMap[mode] || 'Banknote';
  };

  const columns: TableColumn<SalesReportRecord>[] = [
    {
      key: 'id',
      header: 'Sale ID',
      className: 'min-w-[120px]',
      render: (row) => (
        <span className="font-mono text-xs text-neutral-600 whitespace-nowrap">{row.id}</span>
      ),
    },
    {
      key: 'warehouse_name',
      header: 'Warehouse',
      className: 'min-w-[150px]',
      render: (row) => (
        <span className="break-words">{row.warehouse_name}</span>
      ),
    },
    {
      key: 'sale_date',
      header: 'Date',
      render: (row) => new Date(row.sale_date).toLocaleDateString(),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => getStatusBadge(row.status),
    },
    {
      key: 'payment_mode',
      header: 'Payment',
      render: (row) => (
        <div className="flex items-center gap-1 text-sm text-neutral-600">
          <Icon name={getPaymentIcon(row.payment_mode)} size={14} />
          <span className="capitalize">{row.payment_mode}</span>
        </div>
      ),
    },
    {
      key: 'item_count',
      header: 'Items',
      render: (row) => row.item_count,
    },
    {
      key: 'total_amount',
      header: 'Amount',
      render: (row) => (
        <span className="font-medium text-neutral-900">₹{row.total_amount.toLocaleString()}</span>
      ),
    },
    {
      key: 'total_tax',
      header: 'Tax',
      render: (row) => <span className="text-neutral-600">₹{row.total_tax.toLocaleString()}</span>,
    },
    {
      key: 'total_margin',
      header: 'Margin',
      render: (row) => (
        <span className={row.total_margin >= 0 ? 'text-success-600 font-medium' : 'text-error-600'}>
          ₹{row.total_margin.toLocaleString()}
        </span>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (row) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setSelectedRecord(row as SalesReportRecord & {
            customer_name?: string;
            landing_price?: number;
            purchase_value?: number;
            cgst_amount?: number;
            sgst_amount?: number;
            igst_amount?: number;
            tax_exempt_amount?: number;
            effective_tax_rate?: number;
            gross_margin_percent?: number;
            per_unit_margin?: number;
          })}
          className="text-neutral-600 hover:text-primary-600"
        >
          <Icon name="Eye" size={16} />
        </Button>
      ),
    },
  ];

  const summary = report?.summary;
  const summaryCards = summary
    ? [
        {
          label: 'Total Sales',
          value: summary.total_sales,
          icon: 'ShoppingCart' as const,
          color: 'primary' as const,
          format: 'number' as const,
        },
        {
          label: 'Total Revenue',
          value: summary.total_revenue,
          icon: 'IndianRupee' as const,
          color: 'success' as const,
          format: 'currency' as const,
        },
        {
          label: 'Total Tax',
          value: summary.total_tax,
          icon: 'Receipt' as const,
          color: 'info' as const,
          format: 'currency' as const,
        },
        {
          label: 'Total Margin',
          value: summary.total_margin,
          icon: 'TrendingUp' as const,
          color: 'warning' as const,
          format: 'currency' as const,
        },
      ]
    : [];

  // Get total count from API response if available
  const totalItems = report?.pagination?.total || (report?.records?.length || 0);

  const statusTabs = [
    { key: 'all', label: 'All' },
    { key: 'pending', label: 'Pending' },
    { key: 'completed', label: 'Completed' },
    { key: 'cancelled', label: 'Cancelled' },
  ];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/reports')}>
            <Icon name="ArrowLeft" size={16} className="mr-1" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-neutral-900">Sales Report</h1>
            <p className="text-neutral-600 text-sm mt-1">
              Sales transactions with revenue, tax, and margin analysis
            </p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <ReportFilters
        showDateRange={true}
        showSearch={false}
        showWarehouse={true}
        warehouses={warehouseOptions}
        onFilterChange={handleFilterChange}
        onExport={handleExport}
        isExporting={isExporting}
      />

      {/* Status Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-4">
        <div className="flex gap-2 flex-wrap">
          {statusTabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => handleTabChange(tab.key)}
              className={`px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${
                activeTab === tab.key
                  ? 'bg-primary-600 text-white shadow-sm'
                  : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Summary Cards */}
      {!isLoading && !error && summary && <ReportSummaryCards cards={summaryCards} />}

      {/* Payment Mode Breakdown */}
      {!isLoading && !error && summary && (
        <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
          <h3 className="text-sm font-semibold text-neutral-700 mb-4">Payment Breakdown</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(summary.by_payment_mode).map(([mode, value]) => (
              <div key={mode} className="flex items-center gap-3">
                <div className="bg-neutral-100 p-2 rounded-lg">
                  <Icon name={getPaymentIcon(mode)} size={20} className="text-neutral-600" />
                </div>
                <div>
                  <p className="text-xs text-neutral-500 capitalize">{mode}</p>
                  <p className="text-lg font-semibold text-neutral-900">
                    ₹{(value || 0).toLocaleString()}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Report Table */}
      <div className="bg-white rounded-lg shadow-sm border border-neutral-200 overflow-hidden">
        <ReportTable
          columns={columns}
          data={report?.records || []}
          isLoading={isLoading}
          error={error}
          onRetry={refetch}
          emptyMessage="No sales found matching your criteria"
        />
        {!isLoading && !error && (
          <Pagination
            currentPage={currentPage}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={setItemsPerPage}
            disabled={isLoading}
          />
        )}
      </div>

      {/* Detail Modal */}
      <Modal
        isOpen={!!selectedRecord}
        onClose={() => setSelectedRecord(null)}
        title="Sale Details"
        size="lg"
      >
        {selectedRecord && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-neutral-500 mb-1">Sale ID</p>
                <p className="text-sm font-medium text-neutral-900 font-mono">{selectedRecord.id}</p>
              </div>
              <div>
                <p className="text-xs text-neutral-500 mb-1">Warehouse</p>
                <p className="text-sm font-medium text-neutral-900">{selectedRecord.warehouse_name}</p>
                <p className="text-xs text-neutral-500 font-mono">{selectedRecord.warehouse_id}</p>
              </div>
              {selectedRecord.customer_name && (
                <div>
                  <p className="text-xs text-neutral-500 mb-1">Customer Name</p>
                  <p className="text-sm font-medium text-neutral-900">{selectedRecord.customer_name}</p>
                </div>
              )}
              {selectedRecord.customer_id && (
                <div>
                  <p className="text-xs text-neutral-500 mb-1">Customer ID</p>
                  <p className="text-sm font-medium text-neutral-900 font-mono">{selectedRecord.customer_id}</p>
                </div>
              )}
              <div>
                <p className="text-xs text-neutral-500 mb-1">Sale Date</p>
                <p className="text-sm text-neutral-900">{new Date(selectedRecord.sale_date).toLocaleString()}</p>
              </div>
              <div>
                <p className="text-xs text-neutral-500 mb-1">Status</p>
                {getStatusBadge(selectedRecord.status)}
              </div>
              <div>
                <p className="text-xs text-neutral-500 mb-1">Payment Mode</p>
                <div className="flex items-center gap-1 text-sm text-neutral-600">
                  <Icon name={getPaymentIcon(selectedRecord.payment_mode)} size={14} />
                  <span className="capitalize">{selectedRecord.payment_mode}</span>
                </div>
              </div>
              <div>
                <p className="text-xs text-neutral-500 mb-1">Sale Type</p>
                <p className="text-sm text-neutral-900 capitalize">{selectedRecord.sale_type}</p>
              </div>
            </div>

            <div className="border-t border-neutral-200 pt-4">
              <h3 className="text-sm font-semibold text-neutral-700 mb-3">Financial Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-neutral-500 mb-1">Total Amount</p>
                  <p className="text-sm font-medium text-neutral-900">₹{selectedRecord.total_amount.toLocaleString()}</p>
                </div>
                {selectedRecord.landing_price !== undefined && (
                  <div>
                    <p className="text-xs text-neutral-500 mb-1">Landing Price</p>
                    <p className="text-sm font-medium text-neutral-900">₹{selectedRecord.landing_price.toLocaleString()}</p>
                  </div>
                )}
                {selectedRecord.purchase_value !== undefined && (
                  <div>
                    <p className="text-xs text-neutral-500 mb-1">Purchase Value</p>
                    <p className="text-sm font-medium text-neutral-900">₹{selectedRecord.purchase_value.toLocaleString()}</p>
                  </div>
                )}
                <div>
                  <p className="text-xs text-neutral-500 mb-1">Total Tax</p>
                  <p className="text-sm font-medium text-neutral-900">₹{selectedRecord.total_tax.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-xs text-neutral-500 mb-1">Total Margin</p>
                  <p className={`text-sm font-medium ${selectedRecord.total_margin >= 0 ? 'text-success-600' : 'text-error-600'}`}>
                    ₹{selectedRecord.total_margin.toLocaleString()}
                  </p>
                </div>
                {selectedRecord.gross_margin_percent !== undefined && (
                  <div>
                    <p className="text-xs text-neutral-500 mb-1">Gross Margin %</p>
                    <p className={`text-sm font-medium ${selectedRecord.gross_margin_percent >= 0 ? 'text-success-600' : 'text-error-600'}`}>
                      {selectedRecord.gross_margin_percent.toFixed(2)}%
                    </p>
                  </div>
                )}
                {selectedRecord.per_unit_margin !== undefined && (
                  <div>
                    <p className="text-xs text-neutral-500 mb-1">Per Unit Margin</p>
                    <p className={`text-sm font-medium ${selectedRecord.per_unit_margin >= 0 ? 'text-success-600' : 'text-error-600'}`}>
                      ₹{selectedRecord.per_unit_margin.toLocaleString()}
                    </p>
                  </div>
                )}
                <div>
                  <p className="text-xs text-neutral-500 mb-1">Item Count</p>
                  <p className="text-sm font-medium text-neutral-900">{selectedRecord.item_count}</p>
                </div>
              </div>
            </div>

            {selectedRecord.apply_taxes && (
              <div className="border-t border-neutral-200 pt-4">
                <h3 className="text-sm font-semibold text-neutral-700 mb-3">Tax Breakdown</h3>
                <div className="grid grid-cols-2 gap-4">
                  {selectedRecord.cgst_amount !== undefined && (
                    <div>
                      <p className="text-xs text-neutral-500 mb-1">CGST Amount</p>
                      <p className="text-sm font-medium text-neutral-900">₹{selectedRecord.cgst_amount.toLocaleString()}</p>
                    </div>
                  )}
                  {selectedRecord.sgst_amount !== undefined && (
                    <div>
                      <p className="text-xs text-neutral-500 mb-1">SGST Amount</p>
                      <p className="text-sm font-medium text-neutral-900">₹{selectedRecord.sgst_amount.toLocaleString()}</p>
                    </div>
                  )}
                  {selectedRecord.igst_amount !== undefined && selectedRecord.igst_amount > 0 && (
                    <div>
                      <p className="text-xs text-neutral-500 mb-1">IGST Amount</p>
                      <p className="text-sm font-medium text-neutral-900">₹{selectedRecord.igst_amount.toLocaleString()}</p>
                    </div>
                  )}
                  {selectedRecord.tax_exempt_amount !== undefined && selectedRecord.tax_exempt_amount > 0 && (
                    <div>
                      <p className="text-xs text-neutral-500 mb-1">Tax Exempt Amount</p>
                      <p className="text-sm font-medium text-neutral-900">₹{selectedRecord.tax_exempt_amount.toLocaleString()}</p>
                    </div>
                  )}
                  {selectedRecord.effective_tax_rate !== undefined && (
                    <div>
                      <p className="text-xs text-neutral-500 mb-1">Effective Tax Rate</p>
                      <p className="text-sm font-medium text-neutral-900">{selectedRecord.effective_tax_rate.toFixed(2)}%</p>
                    </div>
                  )}
                  <div>
                    <p className="text-xs text-neutral-500 mb-1">Taxes Applied</p>
                    <Badge variant={selectedRecord.apply_taxes ? 'info' : 'neutral'}>
                      {selectedRecord.apply_taxes ? 'Yes' : 'No'}
                    </Badge>
                  </div>
                </div>
              </div>
            )}

            {selectedRecord.status === 'cancelled' && (
              <div className="border-t border-neutral-200 pt-4">
                <h3 className="text-sm font-semibold text-neutral-700 mb-3">Cancellation Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  {selectedRecord.cancelled_at && (
                    <div>
                      <p className="text-xs text-neutral-500 mb-1">Cancelled At</p>
                      <p className="text-sm text-neutral-900">{new Date(selectedRecord.cancelled_at).toLocaleString()}</p>
                    </div>
                  )}
                  {selectedRecord.cancellation_reason && (
                    <div>
                      <p className="text-xs text-neutral-500 mb-1">Cancellation Reason</p>
                      <p className="text-sm text-neutral-900">{selectedRecord.cancellation_reason}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="border-t border-neutral-200 pt-4">
              <h3 className="text-sm font-semibold text-neutral-700 mb-3">Timestamps</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-neutral-500 mb-1">Created At</p>
                  <p className="text-sm text-neutral-900">{new Date(selectedRecord.created_at).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-xs text-neutral-500 mb-1">Updated At</p>
                  <p className="text-sm text-neutral-900">{new Date(selectedRecord.updated_at).toLocaleString()}</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
